import { runInAction, action, computed } from 'mobx'
import { type NotificationsApi } from '@/features/notifications/api/notifications-api'
import type { PrimeMemberFn } from '@/features/iam'
import {
  Notification,
  type NotificationImage,
  type NotificationProps,
  type NotificationStatus,
} from './Notification'
import type {
  NotificationResource,
  NotificationImageResource,
  NotificationRealtimeEventResource,
} from '@/features/notifications/api/resources/notification'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { maybeParseDate } from '@/utils/date-util'
import { share, filter, map } from 'rxjs/operators'
import { type Observable } from 'rxjs'
import { mapNotificationActionResourceToAction } from './notification-mappers'

/**
 * Query for listing notifications.
 */
export interface ListNotificationsQuery extends CursorPageQuery {
  /**
   * When omitted, lists notifications across all workspaces.
   */
  workspaceId?: Id
}

/**
 * Store for managing notifications.
 */
export class NotificationStore {
  /**
   * Collection of notifications.
   */
  readonly notifications = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Notification(),
  })

  /**
   * Emits notifications as realtime updates are applied to the store.
   */
  readonly notificationUpdated$: Observable<Notification>

  /**
   * Emits notifications as they are sent.
   */
  readonly newNotifications$: Observable<Notification>

  constructor(
    private readonly _api: NotificationsApi,
    private readonly _primeMember: PrimeMemberFn,
  ) {
    this.notificationUpdated$ = this._api.notifications.events$.pipe(
      filter(
        (e): e is NotificationRealtimeEventResource =>
          e.type === 'NotificationUpdated',
      ),
      map((e) =>
        this.notifications.set(this._mapNotificationResourceToProps(e.data)),
      ),
      share(),
    )

    this.newNotifications$ = this.notificationUpdated$.pipe(
      filter((n) => n.status === 'Sent'),
    )

    this.notificationUpdated$.subscribe()
  }

  /**
   * Gets the unread notifications count across loaded notifications.
   */
  @computed
  get unreadCount() {
    return this.notifications.filter((n) => n.status !== 'Read').length
  }

  /**
   * Lists notifications for the current user.
   *
   * @param query
   * @param signal
   */
  async listNotifications(
    query: ListNotificationsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Notification>> {
    const response = await this._api.notifications.list(
      {
        after: query.after,
        limit: query.limit,
        workspace_id: query.workspaceId,
      },
      signal,
    )

    return {
      next: response.cursor,
      total: response.total,
      data: this.notifications.set(
        response.data.map((r) => this._mapNotificationResourceToProps(r)),
      ),
    }
  }

  /**
   * Marks the notification as read, restoring its status if that fails.
   *
   * @param notification
   */
  async markAsRead(notification: Notification) {
    if (notification.status === 'Read') {
      return
    }

    const prev = notification.status
    notification.set({ status: 'Read' })
    await this._changeStatus(Id.assert(notification.id), 'Read').catch(
      (err: unknown) => {
        notification.set({ status: prev })
        throw err
      },
    )
  }

  private async _changeStatus(id: string, status: NotificationStatus) {
    const resource = await this._api.notifications.changeStatus({
      id,
      status,
    })
    return resource
      ? this.notifications.set(this._mapNotificationResourceToProps(resource))
      : null
  }

  /**
   * Marks the loaded notifications in the specified workspace as read, or
   * those in every workspace when passed `Workspace.none`.
   *
   * @param workspace
   */
  async markAllAsRead(workspace: Workspace) {
    const toMark = this.notifications.filter(
      (x) =>
        x.status !== 'Read' &&
        (workspace === Workspace.none || x.recipient.workspace === workspace),
    )
    const prev: [Id, NotificationStatus][] = toMark.map((n) => [n.id, n.status])
    runInAction(() => {
      toMark.forEach((n) => {
        n.set({ status: 'Read' })
      })
    })
    return this._api.notifications
      .changeStatusBulk(
        toMark.map((x) => ({ id: Id.assert(x.id), status: 'Read' })),
      )
      .then((data) =>
        this.notifications.set(
          data.map((r) => this._mapNotificationResourceToProps(r)),
        ),
      )
      .catch(
        // Restores status if it failed.
        action((err: unknown) => {
          prev.forEach(([id, status]) => {
            this.notifications.get(id)?.set({ status })
          })
          throw err
        }),
      )
  }

  private _mapNotificationResourceToProps(
    resource: NotificationResource,
  ): Partial<NotificationProps> {
    return {
      id: resource.id,
      key: resource.key,
      message: resource.message,
      status: resource.status,
      action: resource.action
        ? mapNotificationActionResourceToAction(resource.action)
        : undefined,
      image: resource.image
        ? this._mapNotificationImageResourceToImage(resource, resource.image)
        : undefined,
      dateDispatch: maybeParseDate(resource.date_dispatch),
      recipient: this._primeMember({
        workspaceId: Id(resource.workspace_id),
        userId: Id(resource.user_id),
        userPublicId: Id(resource.user_public_id),
      }),
    }
  }

  private _mapNotificationImageResourceToImage(
    resource: NotificationResource,
    image: NotificationImageResource,
  ): NotificationImage {
    switch (image.type) {
      case 'None':
        return { type: 'None' }
      case 'Static':
        return { type: 'Static', url: image.url }
      case 'UserAvatar':
        return {
          type: 'UserAvatar',
          member: this._primeMember({
            workspaceId: Id(resource.workspace_id),
            userId: Id(image.user_id),
            userPublicId: Id(image.user_public_id),
          }),
        }
    }
    // The API may send values this build doesn't know yet.
    return { type: 'None' }
  }
}
